import React, { useEffect, useState } from "react";
import DarkModeContext from "../Contexts/DarkModeContext";

// Local-only until Settings > Account Preferences' own fetch syncs it with
// the server value (see AccountPreferenceTab.jsx) — just avoids a flash of
// the wrong theme before that fetch resolves.
const THEME_STORAGE_KEY = "myfit.themePreference";

function computeIsDark(preference) {
  if (preference === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return preference === "dark";
}

function Default({ children }) {
  // `themePreference` is the user's choice ("system" | "light" | "dark" —
  // same 3 values as myfit-app's AccountPreferencesScreen); `theme` is the
  // resolved light/dark appearance actually applied to the page.
  const [themePreference, setThemePreferenceState] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "system"
  );
  const [theme, setTheme] = useState(() => (computeIsDark(themePreference) ? "dark" : "light"));

  useEffect(() => {
    setTheme(computeIsDark(themePreference) ? "dark" : "light");
    if (themePreference !== "system") return undefined;
    // Only "system" needs to react to the OS changing out from under it.
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setTheme(mql.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [themePreference]);

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const setThemePreference = (value) => {
    localStorage.setItem(THEME_STORAGE_KEY, value);
    setThemePreferenceState(value);
  };

  // Header.jsx's existing icon toggle — flips between the two explicit
  // values; never used to leave "system" (only Account Preferences does).
  const handleThemeSwitch = () => {
    setThemePreference(theme === "dark" ? "light" : "dark");
  };

  return (
    <>
      <DarkModeContext.Provider
        value={{ theme, themePreference, setThemePreference, handleThemeSwitch }}
      >
        {children && children}
      </DarkModeContext.Provider>
    </>
  );
}

export default Default;
